import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { getNotificationPermissionState, requestNotifyPermission } from "@/hooks/useUnreadNotifier";
import { User as UserIcon, Save, Mail, Bell, BellOff, BellRing } from "lucide-react";

/**
 * Per-user profile. Currently exposes the single high-value field:
 * the personal email signature that gets stacked ABOVE the global
 * Edenstone logo on every outbound email the user triggers (marketing
 * sends, transactional welcomes, etc.).
 *
 * Designed to grow — drop in phone number, title, avatar etc here.
 */
export default function ProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const [signature, setSignature] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me/signature")
      .then(({ data }) => { setSignature(data?.Email_Signature || ""); setLoaded(true); })
      .catch((e) => { setLoaded(true); toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me/signature", { Email_Signature: signature });
      setSignature(data?.Email_Signature || "");
      toast.success("Signature saved. It will be used on your next outgoing email.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const charCount = signature.length;
  const overLimit = charCount > 2000;

  // Desktop notification opt-in state. We don't auto-prompt — the user
  // explicitly clicks the button here, which the browser respects much more
  // than an unprompted first-visit nag.
  const [notifyState, setNotifyState] = useState(getNotificationPermissionState());
  const enableNotifications = async () => {
    const result = await requestNotifyPermission();
    setNotifyState(result);
    if (result === "granted") toast.success("Desktop notifications enabled.");
    else if (result === "denied") toast.error("Notifications were blocked. Re-enable them in your browser's site settings.");
    else toast.info("Permission request dismissed.");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">My Profile</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Personalise the details that go out with every email you send from Edenstone Ops.
        </p>
      </div>

      <section className="es-card overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-4 h-4 text-brand-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-zinc-900">Account</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              These details are managed by an admin in <em>Settings → User Management</em>.
            </p>
          </div>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReadOnly label="Full name" value={user?.full_name || "—"} />
          <ReadOnly label="Email" value={user?.email || "—"} icon={Mail} />
          <ReadOnly label="Role" value={user?.role || "—"} mono />
          <ReadOnly label="Member since" value={(user?.created_date || "").slice(0, 10) || "—"} mono />
        </div>
      </section>

      <section className="es-card overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4 text-brand-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-zinc-900">My email signature</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Appears <strong>above</strong> the global Edenstone logo on every outgoing email <em>you</em> send.
              Leave blank to use only the company logo.
            </p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <label className="es-label flex items-center justify-between" htmlFor="profile-signature">
              <span>Signature text (plain text, one detail per line)</span>
              <span className={`text-[10px] font-mono ${overLimit ? "text-red-600" : "text-zinc-400"}`}>
                {charCount}/2000
              </span>
            </label>
            <textarea
              id="profile-signature"
              data-testid="profile-signature-input"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              rows={6}
              placeholder={"Sarah Smith\nOperations Manager\nEdenstone Masonry\n0412 345 678"}
              disabled={!loaded}
              className="es-input h-auto py-2.5 font-mono text-sm resize-y"
            />
          </div>

          {/* Live preview */}
          <div>
            <div className="es-overline mb-2">Preview</div>
            <div
              data-testid="profile-signature-preview"
              className="border border-zinc-200 rounded-lg p-4 bg-white text-[13px] text-zinc-700 whitespace-pre-wrap"
            >
              <p className="text-zinc-900 mb-3">
                Hi there,<br/>
                Just touching base about the order…<br/>
              </p>
              <p className="text-zinc-500 italic mb-3">— Edenstone Masonry Ops</p>
              <div className="border-t border-zinc-100 pt-3 text-xs text-zinc-600 leading-relaxed">
                {signature.trim() || <span className="text-zinc-300">(no personal signature yet)</span>}
              </div>
              <div className="mt-3 inline-block bg-zinc-100 text-zinc-400 text-[10px] font-mono px-2 py-1 rounded">
                + global Edenstone logo image
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              data-testid="profile-signature-save"
              onClick={save}
              disabled={saving || overLimit || !loaded}
              className="es-btn-primary"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save signature"}
            </button>
          </div>
        </div>
      </section>

      <NotificationsCard state={notifyState} onEnable={enableNotifications} />
    </div>
  );
}

function NotificationsCard({ state, onEnable }) {
  const granted = state === "granted";
  const denied = state === "denied";
  const Icon = granted ? BellRing : denied ? BellOff : Bell;
  const color = granted ? "text-emerald-600" : denied ? "text-red-500" : "text-brand-700";
  const ring = granted ? "ring-emerald-200 bg-emerald-50" : denied ? "ring-red-200 bg-red-50" : "ring-brand-200 bg-brand-50";
  return (
    <section className="es-card overflow-hidden" data-testid="profile-notifications-card">
      <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ring-1 ${ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-zinc-900">Desktop notifications</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Get an OS-level toast when a new chat message arrives — even if Edenstone Ops is sitting in a background tab.
            We&apos;ll only notify you when the app isn&apos;t already focused, so it never gets noisy.
          </p>
        </div>
      </div>
      <div className="px-6 py-5">
        {granted && (
          <div className="text-xs text-emerald-700 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200">
            <BellRing className="w-3.5 h-3.5" /> Notifications are enabled in this browser.
          </div>
        )}
        {denied && (
          <div className="text-xs text-red-800 leading-relaxed px-3 py-2 rounded-md bg-red-50 border border-red-200">
            Notifications are blocked. Click the padlock / site-settings icon in your browser&apos;s address bar
            for <span className="font-mono">{window.location.host}</span> and set Notifications to <em>Allow</em> to re-enable.
          </div>
        )}
        {state === "default" && (
          <div className="space-y-3">
            <div className="text-xs text-zinc-600">
              You haven&apos;t opted in yet. Click below and your browser will ask once.
            </div>
            <button
              type="button"
              data-testid="profile-enable-notifications"
              onClick={onEnable}
              className="es-btn-primary"
            >
              <Bell className="w-4 h-4" /> Enable desktop notifications
            </button>
          </div>
        )}
        <p className="text-[10px] text-zinc-500 mt-3">
          You&apos;ll also see a red dot on the browser tab favicon and an unread-count in the tab title whenever the app is open in any tab.
          Those work without granting permission.
        </p>
      </div>
    </section>
  );
}

function ReadOnly({ label, value, mono, icon: Icon }) {
  return (
    <div>
      <label className="es-label">{label}</label>
      <div className="es-input flex items-center gap-2 bg-zinc-50 text-zinc-700">
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400" />}
        <span className={mono ? "font-mono text-xs" : ""}>{value}</span>
      </div>
    </div>
  );
}
